import { z } from 'zod';
import { dateOnlySchema } from './common';
/**
 * Subscriptions: a document that repeats.
 *
 * ── WHAT A PROFILE IS AND IS NOT ──
 * It is a standing instruction to raise a DRAFT, not an instruction to bill
 * anybody. The scan creates the draft and stops; a person issues it. That is
 * not timidity - an issued invoice is a tax point, a GSTR-1 row and a
 * receivable, and none of those should come into being because a timer fired
 * while nobody was looking. `recurring_profiles.auto_send` exists in the
 * schema from 0001 and is deliberately not in this schema: there is no way to
 * ask for it over the wire, so nothing can turn it on by accident.
 *
 * The frequencies are the ones the table has carried since 0001.
 * `half_yearly` is what a person calls semi-annual, and `intervalCount` covers
 * "every two months" without needing a frequency of its own.
 */
export const RECURRING_FREQUENCIES = ['weekly', 'monthly', 'quarterly', 'half_yearly', 'yearly'];
export const RECURRING_STATUSES = ['active', 'paused', 'expired'];
/** What a person calls each one, for a sentence the screen can print. */
export const RECURRING_FREQUENCY_LABELS = {
    weekly: 'Weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    half_yearly: 'Half-yearly',
    yearly: 'Yearly',
};
const profileFields = {
    /**
     * What the person calls this subscription - "Hetzner CX42, re-billed to
     * Acme". It names the schedule, not the document: every draft it raises
     * carries the customer's name anyway, and a list of six rows all reading
     * "Invoice to Acme" is a list nobody can scan.
     */
    profileName: z.string().trim().min(1, 'Give the subscription a name').max(160),
    frequency: z.enum(RECURRING_FREQUENCIES),
    /** Every N periods. 2 with `monthly` is every other month. */
    intervalCount: z.number().int().min(1).max(52).optional(),
    /**
     * The first date an invoice falls due on. May be in the past: a
     * subscription that started in April and is being recorded in October
     * should catch up, and the scan is what decides how far (capped, and it
     * says so rather than silently dropping the rest).
     */
    startDate: dateOnlySchema,
    /** The last date an occurrence may fall ON. Null runs until paused. */
    endDate: dateOnlySchema.nullable().optional(),
    /** Payment term for the invoices it raises, in days from the invoice date. */
    netDays: z.number().int().min(0).max(365).optional(),
    /**
     * Raise the draft this many days BEFORE the invoice date, so it is in the
     * Inbox with time to check it. The draft still carries the real date - this
     * moves when a person sees it, never what the document says.
     */
    leadDays: z.number().int().min(0).max(60).optional(),
};
/**
 * Made FROM an invoice, not typed from nothing.
 *
 * The lines, the customer, the taxes, the terms and the currency all come
 * from a document that already exists and was already priced - which is why
 * there is no line editor here. "Repeat this" on an invoice is the whole
 * creation path, and it means a subscription cannot be set up with a shape
 * the pricing engine has never seen.
 */
export const createRecurringProfileSchema = z
    .object({
    ...profileFields,
    /** The invoice to copy. Its customer, lines, terms and currency become the template. */
    fromInvoiceId: z.string().trim().min(1).max(36),
})
    .strict()
    .refine((v) => !v.endDate || v.endDate >= v.startDate, {
    message: 'The end date cannot be before the start date',
    path: ['endDate'],
});
/**
 * `startDate` is absent on purpose: moving the start of a schedule that has
 * already raised documents would renumber occurrences that exist. Pause it
 * and make another instead.
 */
export const updateRecurringProfileSchema = z
    .object({
    profileName: profileFields.profileName.optional(),
    frequency: profileFields.frequency.optional(),
    intervalCount: profileFields.intervalCount,
    endDate: profileFields.endDate,
    netDays: profileFields.netDays,
    leadDays: profileFields.leadDays,
    /** 'expired' is reached by the end date, never asked for. */
    status: z.enum(['active', 'paused']).optional(),
})
    .strict();
export const listRecurringProfilesQuerySchema = z
    .object({
    status: z.enum([...RECURRING_STATUSES, 'all']).optional(),
})
    .strict();
//# sourceMappingURL=recurring.js.map